/**
 * Transacciones del turno (`/reportes/turnos` → «Transacciones»).
 *
 * Puerto del diálogo del panel anterior (`src/liquidaciones/index.js`,
 * `mostrarTransaccionesTurno`): las ventas del controlador durante el turno, con
 * **buscador** («Transacción, documento, producto…»), filtro por **tipo de
 * documento** (factura, boleta, nota de venta, consumo interno, calibraciones,
 * créditos, pago adelantado y «solo con descuento») y las dos pestañas del
 * original: el listado de transacciones y la **productividad por lado y
 * producto** (con sus contómetros y la exportación).
 *
 * Diferencias anotadas:
 * - El panel anterior usaba `DataTables` (buscador y paginación propios); aquí se
 *   usan los componentes del panel (`Input`, `Select` y `Paginador`), así que el
 *   buscador filtra lo mismo que el de DataTables (todas las columnas de la fila)
 *   y la tabla se pagina igual.
 * - La productividad se calcula en el navegador con las mismas reglas
 *   (`lib/turno-transacciones.ts`) porque las transacciones ya vienen en el
 *   turno; el original también la calculaba en el navegador.
 * - La exportación de la productividad a Excel del original se hace aquí como
 *   **CSV** del propio panel (Excel lo abre igual), y se añade la del listado
 *   filtrado.
 */

import { Download, Info, Search } from 'lucide-react';
import { useMemo, useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Paginador } from '@/components/ui/paginador';
import { Select } from '@/components/ui/select';
import { usePaginacion } from '@/hooks/use-paginacion';
import { exportarCsv } from '@/lib/export';
import { cn, formatCurrency, formatNumber, formatVolumen } from '@/lib/utils';

import {
  TIPOS_TRANSACCION,
  documentoDeTransaccion,
  fechaDeTransaccion,
  filtrarTransacciones,
  productividadPorLado,
  type FiltrosTransacciones,
} from '../lib/turno-transacciones';
import type { TurnoLiquidacion } from '../reportes.api';

const FILTROS_INICIALES: FiltrosTransacciones = { busqueda: '', tipo: '' };

/** Pestañas del diálogo, como las del panel anterior. */
const PESTANAS = [
  { valor: 'transacciones', etiqueta: 'Transacciones' },
  { valor: 'productividad', etiqueta: 'Productividad por lado' },
] as const;

type Pestana = (typeof PESTANAS)[number]['valor'];

/** Nombre del trabajador del turno. */
function nombreDe(turno: TurnoLiquidacion): string {
  return `${turno.first_name ?? ''} ${turno.last_name ?? ''}`.trim() || `Turno #${turno.id}`;
}

export interface TurnoTransaccionesDialogProps {
  turno: TurnoLiquidacion | null;
  onClose: () => void;
}

export function TurnoTransaccionesDialog({ turno, onClose }: TurnoTransaccionesDialogProps) {
  const [pestana, setPestana] = useState<Pestana>('transacciones');
  const [filtros, setFiltros] = useState<FiltrosTransacciones>(FILTROS_INICIALES);

  const transacciones = turno?.transactions ?? [];
  const filtradas = useMemo(() => filtrarTransacciones(transacciones, filtros), [transacciones, filtros]);
  const paginacion = usePaginacion(filtradas, { clave: filtros });

  const { filas: productividad, totales } = useMemo(
    () => productividadPorLado(transacciones),
    [transacciones],
  );

  /** Totales del turno (los mismos contadores del panel anterior). */
  const resumen = useMemo(
    () =>
      transacciones.reduce(
        (suma, transaccion) => ({
          transacciones: suma.transacciones + 1,
          monto: suma.monto + Number(transaccion.amountTransaction ?? 0),
          volumen: suma.volumen + Number(transaccion.volumeTransaction ?? 0),
          descuento: suma.descuento + Number(transaccion.discountTransaction ?? 0),
        }),
        { transacciones: 0, monto: 0, volumen: 0, descuento: 0 },
      ),
    [transacciones],
  );

  const cambiar = <C extends keyof FiltrosTransacciones>(campo: C, valor: FiltrosTransacciones[C]) =>
    setFiltros((previos) => ({ ...previos, [campo]: valor }));

  const exportarListado = () => {
    exportarCsv(
      `transacciones-turno-${turno?.id ?? ''}`,
      ['Transacción', 'Fecha', 'Documento', 'Lado', 'Producto', 'Precio', 'Volumen', 'Monto', 'Descuento', 'Contómetro'],
      filtradas.map((transaccion) => [
        transaccion.idTransaction ?? '',
        fechaDeTransaccion(transaccion),
        documentoDeTransaccion(transaccion),
        transaccion.pumpTransaction ?? '',
        transaccion.FuelGradeName ?? '',
        transaccion.priceTransaction ?? '',
        transaccion.volumeTransaction ?? '',
        transaccion.amountTransaction ?? '',
        transaccion.discountTransaction ?? '',
        transaccion.totalVolumeTransaction ?? '',
      ]),
    );
  };

  const exportarProductividad = () => {
    exportarCsv(
      `productividad-turno-${turno?.id ?? ''}`,
      ['Lado', 'Producto', 'Transacciones', 'Contómetro inicial', 'Contómetro final', 'Volumen (gal)', 'Total soles', 'Descuento'],
      [
        ...productividad.map((fila) => [
          fila.lado,
          fila.producto,
          fila.transacciones,
          fila.contometro_inicial.toFixed(3),
          fila.contometro_final.toFixed(3),
          fila.volumen.toFixed(3),
          fila.monto.toFixed(2),
          fila.descuento.toFixed(2),
        ]),
        [
          'TOTALES',
          '',
          totales.transacciones,
          '',
          '',
          totales.volumen.toFixed(3),
          totales.monto.toFixed(2),
          totales.descuento.toFixed(2),
        ],
      ],
    );
  };

  return (
    <Dialog
      open={turno !== null}
      onClose={onClose}
      title={`Transacciones · ${turno ? nombreDe(turno) : ''}`}
      description="Las ventas del controlador durante el turno (una fila por transacción)."
      size="xl"
    >
      {turno ? (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-2 sm:gap-3 xl:grid-cols-4">
            {[
              { etiqueta: 'Transacciones', valor: formatNumber(resumen.transacciones) },
              { etiqueta: 'Total monto', valor: formatCurrency(resumen.monto) },
              { etiqueta: 'Total volumen', valor: `${formatVolumen(resumen.volumen)} gal` },
              { etiqueta: 'Descuentos', valor: formatCurrency(resumen.descuento) },
            ].map((tarjeta) => (
              <div
                key={tarjeta.etiqueta}
                className="rounded-card border border-ink-100 bg-white px-3 py-2 text-center"
              >
                <p className="text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                  {tarjeta.etiqueta}
                </p>
                <p className="text-sm font-bold text-ink-900">{tarjeta.valor}</p>
              </div>
            ))}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {PESTANAS.map((opcion) => (
              <button
                key={opcion.valor}
                type="button"
                aria-pressed={pestana === opcion.valor}
                onClick={() => setPestana(opcion.valor)}
                className={cn(
                  'rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors',
                  pestana === opcion.valor
                    ? 'border-brand-300 bg-brand-50 text-brand-700'
                    : 'border-ink-200 bg-white text-ink-600 hover:border-brand-200 hover:text-brand-700',
                )}
              >
                {opcion.etiqueta}
                {opcion.valor === 'transacciones' ? ` · ${formatNumber(transacciones.length)}` : ''}
                {opcion.valor === 'productividad' ? ` · ${formatNumber(productividad.length)}` : ''}
              </button>
            ))}

            <div className="ml-auto flex flex-wrap items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={exportarListado}
                leftIcon={<Download className="size-3.5" aria-hidden="true" />}
              >
                Exportar listado
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={exportarProductividad}
                leftIcon={<Download className="size-3.5" aria-hidden="true" />}
              >
                Exportar productividad
              </Button>
            </div>
          </div>

          {pestana === 'transacciones' ? (
            <>
              <div className="grid items-end gap-3 rounded-card border border-ink-100 p-3 sm:grid-cols-[1fr_14rem]">
                <div className="flex flex-col gap-1.5 text-sm">
                  <span className="font-medium text-ink-700">Buscar</span>
                  <span className="flex h-11 items-center gap-2 rounded-control border border-ink-200 bg-white px-3 focus-within:border-brand-400 focus-within:ring-4 focus-within:ring-brand-100">
                    <Search className="size-4 text-ink-400" aria-hidden="true" />
                    <input
                      type="search"
                      value={filtros.busqueda}
                      onChange={(evento) => cambiar('busqueda', evento.target.value)}
                      placeholder="Transacción, documento, producto…"
                      className="w-full bg-transparent text-sm text-ink-900 outline-none"
                      aria-label="Buscar transacción"
                    />
                  </span>
                </div>

                <Select
                  value={filtros.tipo}
                  onChange={({ target }) => cambiar('tipo', target.value)}
                  className="h-11 w-full rounded-control border-ink-200 text-sm"
                  aria-label="Tipo de documento"
                >
                  {TIPOS_TRANSACCION.map((opcion) => (
                    <option key={opcion.valor} value={opcion.valor}>
                      {opcion.etiqueta}
                    </option>
                  ))}
                </Select>
              </div>

              <div className="max-h-[50vh] overflow-auto rounded-card border border-ink-100">
                <table className="w-full min-w-[54rem] table-fixed border-collapse text-[13px]">
                  <thead className="sticky top-0 z-10 bg-white shadow-[0_1px_0_0_var(--color-ink-100)]">
                    <tr className="text-left text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                      <th className="w-[4.5rem] px-2 py-2 text-right">Transacción</th>
                      <th className="w-[8rem] px-2 py-2">Fecha</th>
                      <th className="w-[7rem] px-2 py-2">Documento</th>
                      <th className="w-[3.5rem] px-2 py-2 text-right">Lado</th>
                      <th className="px-2 py-2">Producto</th>
                      <th className="w-[5rem] px-2 py-2 text-right">Precio</th>
                      <th className="w-[5rem] px-2 py-2 text-right">Volumen</th>
                      <th className="w-[5.5rem] px-2 py-2 text-right">Monto</th>
                      <th className="w-[5rem] px-2 py-2 text-right">Descuento</th>
                      <th className="w-[6rem] px-2 py-2 text-right">Contómetro</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtradas.length === 0 ? (
                      <tr className="border-t border-ink-100">
                        <td colSpan={10} className="px-2 py-8 text-center text-sm text-ink-500">
                          {transacciones.length === 0
                            ? 'El turno no tiene transacciones registradas.'
                            : 'Ninguna transacción cumple el buscador y el tipo elegidos.'}
                        </td>
                      </tr>
                    ) : null}

                    {paginacion.visibles.map((transaccion, indice) => (
                      <tr
                        key={`${transaccion.idTransaction}-${transaccion.pumpTransaction}-${indice}`}
                        className={cn(
                          'border-t border-ink-100',
                          Number(transaccion.voidedTransaction) === 1 && 'text-ink-400 line-through',
                        )}
                      >
                        <td className="px-2 py-1.5 text-right font-mono text-xs text-ink-700 tabular-nums">
                          {transaccion.idTransaction ?? '—'}
                        </td>
                        <td className="truncate px-2 py-1.5 text-ink-600">
                          {fechaDeTransaccion(transaccion) || '—'}
                        </td>
                        <td className="truncate px-2 py-1.5 font-semibold text-ink-800">
                          {documentoDeTransaccion(transaccion)}
                        </td>
                        <td className="px-2 py-1.5 text-right text-ink-600">
                          {transaccion.pumpTransaction ?? '—'}
                        </td>
                        <td className="truncate px-2 py-1.5 text-ink-800">
                          {transaccion.FuelGradeName ?? '—'}
                          {transaccion.calibrationTransaction ? (
                            <Badge variant="outline" className="ml-1.5 px-1.5 py-0 text-[10px]">
                              Calibración #{transaccion.calibrationTransaction}
                            </Badge>
                          ) : null}
                        </td>
                        <td className="px-2 py-1.5 text-right text-ink-600">
                          {formatNumber(Number(transaccion.priceTransaction ?? 0))}
                        </td>
                        <td className="px-2 py-1.5 text-right text-ink-600">
                          {formatVolumen(Number(transaccion.volumeTransaction ?? 0))}
                        </td>
                        <td className="px-2 py-1.5 text-right font-semibold text-ink-900">
                          {formatCurrency(Number(transaccion.amountTransaction ?? 0))}
                        </td>
                        <td className="px-2 py-1.5 text-right text-ink-600">
                          {formatCurrency(Number(transaccion.discountTransaction ?? 0))}
                        </td>
                        <td className="px-2 py-1.5 text-right text-ink-600">
                          {formatVolumen(Number(transaccion.totalVolumeTransaction ?? 0))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="sticky bottom-0 bg-white shadow-[0_-1px_0_0_var(--color-ink-100)]">
                    <tr className="text-[13px] font-semibold text-ink-800">
                      <td className="px-2 py-2" colSpan={6}>
                        TOTAL · {formatNumber(resumen.transacciones)} transacción(es)
                        {filtradas.length !== transacciones.length
                          ? ` · mostradas ${formatNumber(filtradas.length)}`
                          : ''}
                      </td>
                      <td className="px-2 py-2 text-right">
                        {formatVolumen(resumen.volumen)}
                      </td>
                      <td className="px-2 py-2 text-right">{formatCurrency(resumen.monto)}</td>
                      <td className="px-2 py-2 text-right">
                        {formatCurrency(resumen.descuento)}
                      </td>
                      <td className="px-2 py-2" />
                    </tr>
                  </tfoot>
                </table>
              </div>

              <Paginador {...paginacion} etiqueta="transacciones" />

              <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-ink-600">
                <span className="font-semibold text-ink-800">Por tipo de comprobante</span>
                {turno.resumen_financiero.desglose_series
                  .filter((serie) => serie.cantidad > 0)
                  .map((serie) => (
                    <span key={serie.codigo}>
                      <strong className="font-semibold text-ink-800">{serie.nombre}</strong> (
                      {serie.codigo}) · {formatNumber(serie.cantidad)} ·{' '}
                      {formatCurrency(serie.monto)} · {formatVolumen(serie.volumen)} gal ·{' '}
                      {formatNumber(serie.porcentaje)} %
                    </span>
                  ))}
              </div>

              {turno.resumen_financiero.desglose_descuentos.length > 0 ? (
                <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-ink-600">
                  <span className="font-semibold text-ink-800">Descuentos</span>
                  {turno.resumen_financiero.desglose_descuentos.map((descuento) => (
                    <span key={descuento.tipo}>
                      <strong className="font-semibold text-ink-800">{descuento.nombre}</strong>:{' '}
                      {formatCurrency(descuento.total_descuento)}
                      {descuento.total_sobrecargo > 0
                        ? ` · sobrecargos ${formatCurrency(descuento.total_sobrecargo)}`
                        : ''}
                    </span>
                  ))}
                </div>
              ) : null}
            </>
          ) : (
            <>
              <div className="overflow-x-auto rounded-card border border-ink-100">
                <table className="w-full border-collapse text-[13px]">
                  <thead className="bg-ink-50">
                    <tr className="text-left text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                      <th className="w-[4rem] px-2 py-2 text-right">Lado</th>
                      <th className="px-2 py-2">Producto</th>
                      <th className="w-[6.5rem] px-2 py-2 text-right">Transacciones</th>
                      <th className="w-[7rem] px-2 py-2 text-right">Cont. inicial</th>
                      <th className="w-[7rem] px-2 py-2 text-right">Cont. final</th>
                      <th className="w-[7rem] px-2 py-2 text-right">Volumen (gal)</th>
                      <th className="w-[6.5rem] px-2 py-2 text-right">Total soles</th>
                      <th className="w-[6rem] px-2 py-2 text-right">Descuento</th>
                    </tr>
                  </thead>
                  <tbody>
                    {productividad.length === 0 ? (
                      <tr className="border-t border-ink-100">
                        <td colSpan={8} className="px-2 py-8 text-center text-sm text-ink-500">
                          El turno no tiene transacciones registradas.
                        </td>
                      </tr>
                    ) : null}

                    {productividad.map((fila) => (
                      <tr key={`${fila.lado}-${fila.producto}`} className="border-t border-ink-100">
                        <td className="px-2 py-1.5 text-right font-semibold text-ink-900">
                          {fila.lado}
                        </td>
                        <td className="px-2 py-1.5 font-semibold text-ink-800">{fila.producto}</td>
                        <td className="px-2 py-1.5 text-right text-ink-600">{fila.transacciones}</td>
                        <td className="px-2 py-1.5 text-right text-ink-600 tabular-nums">
                          {formatVolumen(fila.contometro_inicial)}
                        </td>
                        <td className="px-2 py-1.5 text-right text-ink-600 tabular-nums">
                          {formatVolumen(fila.contometro_final)}
                        </td>
                        <td className="px-2 py-1.5 text-right text-ink-700 tabular-nums">
                          {formatVolumen(fila.volumen)}
                        </td>
                        <td className="px-2 py-1.5 text-right font-semibold text-ink-900">
                          {formatCurrency(fila.monto)}
                        </td>
                        <td className="px-2 py-1.5 text-right text-ink-600">
                          {formatCurrency(fila.descuento)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t border-ink-100 text-[13px] font-semibold text-ink-800">
                      <td className="px-2 py-2" colSpan={2}>
                        TOTALES
                      </td>
                      <td className="px-2 py-2 text-right">{totales.transacciones}</td>
                      <td className="px-2 py-2 text-right">—</td>
                      <td className="px-2 py-2 text-right">—</td>
                      <td className="px-2 py-2 text-right tabular-nums">
                        {formatVolumen(totales.volumen)}
                      </td>
                      <td className="px-2 py-2 text-right">{formatCurrency(totales.monto)}</td>
                      <td className="px-2 py-2 text-right">
                        {formatCurrency(totales.descuento)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              <p className="flex items-start gap-2 text-xs text-ink-500">
                <Info className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
                <span>
                  La productividad agrupa las transacciones por <strong>lado</strong> y
                  <strong> producto</strong>: los contómetros salen de la primera y la última
                  transacción del grupo (ordenadas por fecha y hora), no del orden en que llegan del
                  controlador.
                </span>
              </p>
            </>
          )}
        </div>
      ) : null}
    </Dialog>
  );
}
